import React, { useState } from "react";
import { Sparkles, Loader2, MessageSquare, Pencil, Copy } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { feedbackKey, isNegative, useCalloutFeedback } from "@/lib/calloutFeedback";
import { UsageCost } from "@/components/UsageCost";
import { WordingFeedback } from "@/components/WordingFeedback";

const STATUS_STYLES = {
  "addressed":         { label: "Addressed",         badge: "text-emerald-300 border-emerald-700/60 bg-emerald-950/30" },
  "not-addressable":   { label: "Cannot fix",        badge: "text-rose-300 border-rose-700/60 bg-rose-950/30" },
  "accepted-tension":  { label: "Accepted tension",  badge: "text-amber-300 border-amber-700/60 bg-amber-950/30" },
};

// `existing` may be an old single dict {revised_text, ...} or the newer array of versions.
const normaliseHistory = (existing) => {
  if (!existing) return [];
  if (Array.isArray(existing)) return existing;
  if (existing.revised_text) return [{ ...existing, version: 1, created_at: existing.created_at || new Date().toISOString() }];
  return [];
};

/** Version history of LLM revisions for one rule, persisted (append-only) after every change. */
function useRevisionHistory({ existing, persist, savedId, onSave }) {
  const initial = normaliseHistory(existing);
  const [history, setHistory] = useState(initial);
  const [activeIdx, setActiveIdx] = useState(initial.length ? initial.length - 1 : 0);
  const commit = async (next) => {
    setHistory(next);
    setActiveIdx(next.length - 1);
    if (onSave) onSave(next);
    try {
      if (persist) await persist(next);
      else if (savedId) await api.updateProposal(savedId, { revision_suggestion: next });
    } catch (e) {
      console.warn("Failed to persist revision history", e);
    }
  };
  return { history, activeIdx, setActiveIdx, commit, data: history[activeIdx] || null };
}

function HeaderActions({ data, loading, showFeedback, onToggleFeedback, onGenerate }) {
  if (loading) {
    return (
      <span className="ml-auto inline-flex items-center gap-2 text-xs text-violet-300">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting revision…
      </span>
    );
  }
  if (!data) {
    return (
      <Button size="sm" className="ml-auto gap-2" onClick={onGenerate} data-testid="suggest-revision-btn">
        <Sparkles className="w-3.5 h-3.5" /> Suggest revision
      </Button>
    );
  }
  return (
    <div className="ml-auto flex items-center gap-1.5">
      <Button size="sm" variant="outline" className="gap-1.5" onClick={onToggleFeedback} data-testid="suggest-revision-feedback-toggle">
        <MessageSquare className="w-3.5 h-3.5" /> {showFeedback ? "Hide feedback" : "Refine with feedback"}
      </Button>
      <Button size="sm" variant="outline" className="gap-1.5" onClick={onGenerate} data-testid="suggest-revision-regen-btn">
        <Sparkles className="w-3.5 h-3.5" /> Regenerate
      </Button>
    </div>
  );
}

function VersionScrubber({ history, activeIdx, setActiveIdx, data }) {
  if (history.length <= 1) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="revision-version-scrubber">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Versions</span>
      {history.map((h, i) => (
        <button
          key={h.created_at || `v${i}`}
          type="button"
          onClick={() => setActiveIdx(i)}
          className={`px-2 py-0.5 rounded text-[11px] border transition ${i === activeIdx ? "border-violet-600/70 bg-violet-950/40 text-violet-200" : "border-border text-muted-foreground hover:text-foreground"}`}
          data-testid={`revision-version-${i + 1}`}
          title={h.feedback ? `Feedback: ${h.feedback}` : "Initial revision"}
        >
          v{i + 1}{h.feedback ? " ↺" : ""}
        </button>
      ))}
      <span className="text-[10px] text-muted-foreground ml-1">{data?.feedback ? `Refined with: "${data.feedback}"` : "Initial revision"}</span>
    </div>
  );
}

function FeedbackBox({ feedback, setFeedback, loading, onSubmit, onCancel }) {
  return (
    <div className="mb-3 p-3 rounded border border-violet-700/40 bg-violet-950/15 space-y-2" data-testid="revision-feedback-box">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Feedback for the next revision</p>
      <Textarea
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        placeholder='e.g. "stay closer to the original wording" or "use the word ‘place’ instead of ‘move’" or "make it shorter"…'
        className="min-h-[80px] font-sans text-sm"
        disabled={loading}
        data-testid="revision-feedback-textarea"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" className="gap-1.5" onClick={onSubmit} disabled={loading || !feedback.trim()} data-testid="revision-regenerate-with-feedback-btn">
          <Sparkles className="w-3.5 h-3.5" /> Regenerate with feedback
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={loading} data-testid="revision-feedback-cancel">Cancel</Button>
        <span className="text-[10px] text-muted-foreground">The LLM sees all prior versions and your feedback as conversation context.</span>
      </div>
    </div>
  );
}

function RevisedWording({ data, nextVersion, onSaveEdit, ruleType, intent }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  if (editing) {
    return (
      <div className="space-y-2">
        <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} className="min-h-[110px] text-sm" data-testid="revision-edit-textarea" />
        <div className="flex gap-2">
          <Button size="sm" onClick={() => onSaveEdit(editText.trim()).then(() => setEditing(false))} disabled={!editText.trim() || editText.trim() === data.revised_text} data-testid="revision-edit-save">
            Save as v{nextVersion}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="revision-edit-cancel">Cancel</Button>
        </div>
      </div>
    );
  }
  return (
    <>
      <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed p-3 rounded border border-violet-700/40 bg-violet-950/15 select-text" data-testid="revision-revised-text">
        {data.revised_text}
      </pre>
      <div className="flex items-center gap-2 mt-1.5">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditText(data.revised_text); setEditing(true); }} data-testid="revision-edit-btn">
          <Pencil className="w-3 h-3 mr-1" /> Edit this wording
        </Button>
        {data.edited_by_user && <Badge variant="outline" className="text-[10px] text-cyan-300 border-cyan-700/50" data-testid="revision-edited-badge">edited by playtester</Badge>}
      </div>
      <WordingFeedback source="revision" text={data.revised_text} ruleType={ruleType} intent={intent} testId="revision-wording-feedback" />
    </>
  );
}

function ResolutionItem({ r }) {
  const style = STATUS_STYLES[r.status] || { label: r.status, badge: "text-muted-foreground border-border" };
  const steamroller = r.source === "steamroller";
  return (
    <div className="text-sm p-2 rounded border border-border bg-card/40">
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <Badge variant="outline" className={`text-[10px] ${style.badge}`}>{style.label}</Badge>
        <Badge variant="outline" className={`text-[10px] ${steamroller ? "text-amber-300 border-amber-700/60" : "text-cyan-300 border-cyan-700/60"}`}>
          {steamroller ? "Steamroller" : "Main Rulebook"}
        </Badge>
        {r.severity && <Badge variant="outline" className="text-[10px]">{r.severity}</Badge>}
        <span className="font-medium text-sm">{r.section_title || "(no section)"}</span>
      </div>
      {r.note && <p className="text-xs text-muted-foreground leading-relaxed">{r.note}</p>}
    </div>
  );
}

function ResolutionBreakdown({ resolutions }) {
  if (!resolutions?.length) return null;
  const counts = { addressed: 0, "not-addressable": 0, "accepted-tension": 0 };
  for (const r of resolutions) if (r.status in counts) counts[r.status] += 1;
  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Conflict resolution breakdown</p>
        <span className="ml-auto flex items-center gap-1.5 text-[10px]">
          {Object.entries(counts).filter(([, n]) => n > 0).map(([status, n]) => (
            <span key={status} className={`px-1.5 py-0.5 rounded border ${STATUS_STYLES[status].badge}`} data-testid={`revision-count-${status}`}>
              <strong>{n}</strong> {STATUS_STYLES[status].label}
            </span>
          ))}
        </span>
      </div>
      <div className="space-y-1.5" data-testid="revision-resolutions">
        {resolutions.map((r) => <ResolutionItem key={`${r.source}-${r.section_title}-${r.status}`} r={r} />)}
      </div>
    </div>
  );
}

function UseAsSideB({ text, label, onUse }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <Button size="sm" className="gap-2" onClick={() => onUse(text)} data-testid="revision-use-as-side-b-btn">
        <Copy className="w-3.5 h-3.5" /> Use {label} as Side B
      </Button>
      <p className="text-[11px] text-muted-foreground">Loads this wording into the alternative-wording slot so you can A-vs-B it against the original.</p>
    </div>
  );
}

export function RevisionSuggestion({ persist, proposedText, ruleType, modelTypes, intent, ruleStats, conflicts, existing, onSave, onUseAsSideB, savedId }) {
  const { history, activeIdx, setActiveIdx, commit, data } = useRevisionHistory({ existing, persist, savedId, onSave });
  const [feedback, setFeedback] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const verdicts = useCalloutFeedback()?.mine || {};

  if (!conflicts || conflicts.length === 0) return null;
  // Testers' verdicts: rejected callouts are not sent for fixing; all verdicts steer the rewrite.
  const calloutFeedback = conflicts
    .map((c) => ({ c, v: verdicts[feedbackKey.conflict(c)] }))
    .filter(({ v }) => v)
    .map(({ c, v }) => ({ label: `${c.source === "steamroller" ? "Steamroller" : "Rulebook"}: ${c.section_title}`, verdict: v.verdict, reason: v.reason }));
  const activeConflicts = conflicts.filter((c) => !isNegative(verdicts[feedbackKey.conflict(c)]));
  if (activeConflicts.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic" data-testid="revision-all-dismissed">
        Every conflict was marked wrong or dismissed by testers — nothing left for a revised wording to fix.
      </p>
    );
  }

  const generate = async (withFeedback = false) => {
    setLoading(true);
    setError(null);
    try {
      const prior = withFeedback ? history.map((h) => ({ revised_text: h.revised_text, change_summary: h.change_summary })) : [];
      const note = withFeedback ? feedback.trim() : null;
      const r = await api.suggestRevision(proposedText, activeConflicts, ruleType || null, modelTypes || [], note, prior, intent || null, ruleStats || null, calloutFeedback);
      await commit([...history, { ...r, version: history.length + 1, feedback: note, created_at: new Date().toISOString() }]);
      setFeedback("");
      setShowFeedback(false);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to generate revision");
    } finally {
      setLoading(false);
    }
  };
  const saveEdit = (text) => commit([...history, {
    revised_text: text, change_summary: "Edited by the playtester from the LLM suggestion.", resolutions: [],
    edited_by_user: true, version: history.length + 1, created_at: new Date().toISOString(),
  }]);
  const versionLabel = history.length > 1 ? `v${activeIdx + 1}` : "";

  return (
    <Card className="border-violet-700/40" data-testid="revision-suggestion-card">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Sparkles className="w-4 h-4 text-violet-400" />
          <h3 className="font-semibold">Suggested revised wording</h3>
          <span className="text-xs text-muted-foreground">LLM-proposed rewrite that resolves as many conflicts as possible.</span>
          <HeaderActions data={data} loading={loading} showFeedback={showFeedback} onToggleFeedback={() => setShowFeedback((v) => !v)} onGenerate={() => generate(false)} />
        </div>
        <VersionScrubber history={history} activeIdx={activeIdx} setActiveIdx={setActiveIdx} data={data} />
        {showFeedback && data && (
          <FeedbackBox feedback={feedback} setFeedback={setFeedback} loading={loading}
            onSubmit={() => generate(Boolean(feedback.trim()))} onCancel={() => { setShowFeedback(false); setFeedback(""); }} />
        )}
        {error && <div className="mt-2 p-2 rounded border border-rose-700/60 bg-rose-950/15 text-sm text-rose-300" data-testid="revision-error">{error}</div>}
        {data && (
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Revised wording {versionLabel && `· ${versionLabel}`}</p>
              <RevisedWording data={data} nextVersion={history.length + 1} onSaveEdit={saveEdit} ruleType={ruleType} intent={intent} />
            </div>
            <UsageCost usage={data.usage} label={`Revision v${data.version || activeIdx + 1}`} testId="revision-usage-cost" />
            {data.change_summary && (
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">What changed</p>
                <p className="text-sm leading-relaxed" data-testid="revision-change-summary">{data.change_summary}</p>
              </div>
            )}
            <ResolutionBreakdown resolutions={data.resolutions} />
            {onUseAsSideB && <UseAsSideB text={data.revised_text} label={versionLabel || "this"} onUse={onUseAsSideB} />}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
